import React from "react";
import ReactMarkdown from "react-markdown";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";

const AnnouncementBanner: React.FC = () => {
  const [renderedAnnouncement, setRenderedAnnouncement] = React.useState<
    ReturnType<typeof useAuth>["bannerAnnouncement"]
  >(null);
  const [isVisible, setIsVisible] = React.useState(false);
  const navigate = useNavigate();
  const {
    bannerAnnouncement,
    closeBannerAnnouncement,
    dismissBannerAnnouncement,
    trackBannerAnnouncementShown,
    trackBannerAnnouncementClick,
  } = useAuth();

  React.useEffect(() => {
    if (!bannerAnnouncement) {
      return;
    }
    void trackBannerAnnouncementShown();
  }, [bannerAnnouncement?.id]);

  React.useEffect(() => {
    if (bannerAnnouncement) {
      setRenderedAnnouncement(bannerAnnouncement);
      const animationFrame = window.requestAnimationFrame(() => {
        setIsVisible(true);
      });
      return () => {
        window.cancelAnimationFrame(animationFrame);
      };
    }

    if (!renderedAnnouncement) {
      return;
    }

    setIsVisible(false);
    const timeout = window.setTimeout(() => {
      setRenderedAnnouncement(null);
    }, 260);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [bannerAnnouncement, renderedAnnouncement]);

  const activeAnnouncement = bannerAnnouncement ?? renderedAnnouncement;

  if (!activeAnnouncement) {
    return null;
  }

  const handleNavigate = (url: string, inApp: boolean) => {
    if (inApp) {
      navigate(url);
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const hasTitle = Boolean(activeAnnouncement.title);
  const hasBody = Boolean(activeAnnouncement.body);

  return (
    <div
      className={`tw-transition-[max-height,opacity,transform,padding] ${
        isVisible
          ? "tw-max-h-56 tw-overflow-visible tw-opacity-100 tw-translate-y-0 tw-pt-2"
          : "tw-max-h-0 tw-overflow-hidden tw-opacity-0 -tw-translate-y-1 tw-pt-0"
      }`}
      style={{
        backgroundColor: "#f5f5f5",
        transitionDuration: isVisible ? "180ms" : "260ms",
      }}
    >
      <div className="custom-container tw-h-auto">
        <div
          className="tw-mx-auto tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-center tw-gap-2 sm:tw-gap-4 tw-rounded-xl tw-py-px tw-px-3 sm:tw-px-4"
          style={{
            backgroundColor: "#f6e3bf",
            width: "calc(100% - 48px)",
            maxWidth: "1080px",
          }}
        >
          <div
            className="tw-min-w-0 tw-flex-1 tw-border-l-4 tw-pl-3 tw-flex tw-items-center"
            style={{ borderLeftColor: "#11609d" }}
          >
            <div
              className="tw-min-w-0 tw-text-sm tw-leading-5 tw-text-slate-800 tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-1"
              style={{
                overflow: "hidden",
              }}
            >
              <div
                className="tw-min-w-0 sm:tw-max-w-[32rem]"
                style={{
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 2,
                  overflow: "hidden",
                }}
              >
                {hasTitle && (
                  <span className="tw-font-semibold tw-text-slate-950">
                    {activeAnnouncement.title}
                    {hasBody ? ": " : ""}
                  </span>
                )}
                {hasBody && (
                  <ReactMarkdown
                    components={{
                      p: ({ children }) => <span>{children}</span>,
                      a: ({ href, children }) => {
                        const isExternal = Boolean(href && /^https?:\/\//.test(href));
                        return (
                          <a
                            href={href}
                            target={isExternal ? "_blank" : undefined}
                            rel={isExternal ? "noopener noreferrer" : undefined}
                          >
                            {children}
                          </a>
                        );
                      },
                    }}
                  >
                    {activeAnnouncement.body}
                  </ReactMarkdown>
                )}
              </div>

              {activeAnnouncement.cta2_label && activeAnnouncement.cta2_url && (
                <button
                  type="button"
                  onClick={async () => {
                    await trackBannerAnnouncementClick(2);
                    const url = activeAnnouncement.cta2_url as string;
                    handleNavigate(url, url.startsWith("/"));
                  }}
                  className="tw-text-sm tw-font-medium tw-text-[#11609d] tw-underline tw-underline-offset-2 hover:tw-text-[#0b4d7f]"
                >
                  {activeAnnouncement.cta2_label} {"→"}
                </button>
              )}

              {activeAnnouncement.cta1_label && activeAnnouncement.cta1_url && (
                <button
                  type="button"
                  onClick={async () => {
                    await trackBannerAnnouncementClick(1);
                    const url = activeAnnouncement.cta1_url as string;
                    handleNavigate(url, url.startsWith("/"));
                  }}
                  className="tw-text-sm tw-font-medium tw-text-[#11609d] tw-underline tw-underline-offset-2 hover:tw-text-[#0b4d7f]"
                >
                  {activeAnnouncement.cta1_label} {"→"}
                </button>
              )}

              {activeAnnouncement.allow_dismiss !== false && (
                <button
                  type="button"
                  onClick={async () => {
                    await dismissBannerAnnouncement();
                  }}
                  className="tw-text-xs tw-text-[#11609d] tw-underline tw-underline-offset-2 hover:tw-text-[#0b4d7f]"
                >
                  Nicht mehr anzeigen
                </button>
              )}
            </div>
          </div>

          <div className="tw-flex tw-items-center tw-justify-end tw-w-8 tw-flex-shrink-0 tw-ml-4 sm:tw-ml-6">
            {activeAnnouncement.allow_close !== false && (
              <button
                type="button"
                aria-label="Banner schließen"
                onClick={async () => {
                  await closeBannerAnnouncement();
                }}
                className="tw-h-8 tw-w-8 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-text-slate-600 hover:tw-bg-[#dcecf7] hover:tw-text-slate-900"
              >
                ×
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementBanner;
